/* global axios */
import ApiClient from '../ApiClient';

class EnterpriseAccountAPI extends ApiClient {
  constructor() {
    // Use V1 for subscription management (not enterprise namespace)
    super('', { accountScoped: true, enterprise: false, apiVersion: 'v1' });
  }

  /**
   * Open the self-serve billing portal for the current customer
   */
  openBillingPortal() {
    return axios.post(`${this.url}subscriptions/customer_portal`);
  }

  /**
   * Legacy alias retained for older callers.
   * @deprecated Use openBillingPortal() instead
   */
  checkout() {
    return this.openBillingPortal();
  }

  /**
   * Legacy V1 subscription endpoint - redirects to V2
   * @deprecated Use getSubscriptions() instead
   */
  subscription() {
    return this.getSubscriptions();
  }

  getSubscriptions() {
    return axios.get(`${this.url}subscriptions`);
  }

  getSubscription(id) {
    return axios.get(`${this.url}subscriptions/${id}`);
  }

  createSubscription(params) {
    return axios.post(`${this.url}subscriptions`, params);
  }

  updateSubscription(id, params) {
    return axios.patch(`${this.url}subscriptions/${id}`, params);
  }

  /**
   * Change the seat count on the current subscription.
   * Polar prorates the delta for the remaining cycle and bills it immediately.
   */
  updateSubscriptionSeats(id, seats) {
    return axios.patch(`${this.url}subscriptions/${id}/seats`, { seats });
  }

  deleteSubscription(id, reason) {
    return axios.delete(`${this.url}subscriptions/${id}`, {
      data: { reason },
    });
  }

  /**
   * Create a checkout session for subscription or Sage Credits top-up
   */
  createCheckout(params) {
    return axios.post(`${this.url}subscriptions/checkout`, params);
  }

  /**
   * Get customer portal URL
   */
  getCustomerPortal() {
    return axios.post(`${this.url}subscriptions/customer_portal`);
  }

  /**
   * Create a Polar customer
   */
  createCustomer() {
    return axios.post(`${this.url}subscriptions/create_customer`);
  }

  getInvoices() {
    return axios.get(`${this.url}subscriptions/invoices`);
  }

  /**
   * Signed download URL for a paid order's invoice PDF (proxied via the
   * server so the Polar token never reaches the browser).
   */
  getInvoiceUrl(orderId) {
    return axios.get(`${this.url}subscriptions/invoices/${orderId}`);
  }

  getLimits() {
    // Limits is still V1
    const v1Url = this.url.replace('v2/', '');
    return axios.get(`${v1Url}limits`);
  }

  toggleDeletion(action) {
    // Toggle deletion is still V1
    const v1Url = this.url.replace('v2/', '');
    return axios.post(`${v1Url}toggle_deletion`, {
      action_type: action,
    });
  }

  createTopupCheckout(credits, product_id) {
    return axios.post(`${this.url}subscriptions/topup_checkout`, {
      credits,
      product_id,
    });
  }

  /**
   * Credit Packs (Sage AI)
   * New 4-tier pack system (Starter/Growth/Pro/Business).
   * Polar handles all actual billing; we just call checkout with the
   * pack slug and get back a hosted checkout URL.
   */
  getCreditPacks() {
    return axios.get(`${this.url}credit_packs`);
  }

  getCreditBalance() {
    return axios.get(`${this.url}credit_packs/balance`);
  }

  createCreditPackCheckout(packSlug) {
    return axios.post(`${this.url}credit_packs/checkout`, {
      pack_slug: packSlug,
    });
  }

  /**
   * Regenerate checkout session for incomplete trial subscription
   * Creates a fresh checkout URL every time the user clicks "Start Free Trial"
   */
  regenerateCheckout() {
    return axios.post(`${this.url}subscriptions/regenerate_checkout`);
  }

  /**
   * Refresh subscription data from Polar (trust Polar over local DB)
   */
  refreshSubscription() {
    return axios.get(`${this.url}subscriptions/refresh`);
  }

  getAffiliateProgram() {
    return axios.get(`${this.url}affiliate_program`);
  }

  /**
   * Authoritative balances by currency and recent affiliate activity.
   */
  getAffiliateDashboard() {
    return axios.get(`${this.url}affiliate_program/dashboard`);
  }

  joinAffiliateProgram() {
    return axios.post(`${this.url}affiliate_program`);
  }

  updateAffiliateCode(referralCode) {
    return axios.patch(`${this.url}affiliate_program`, {
      referral_code: referralCode,
    });
  }
}

export default new EnterpriseAccountAPI();
